"use client";
import { useState, useEffect, useRef } from "react";

function useLocalStorage<T>(
    key: string,
    initialValue: T
): [T, (value: T) => void] {
    const [storedValue, setStoredValue] = useState<T>(initialValue);
    if (typeof window === "undefined") {
        return [initialValue, () => {}];
    }
    useEffect(() => {
        try {
            const item = window.localStorage.getItem(key);
            if (item) {
                setStoredValue(JSON.parse(item));
            }
        } catch (error) {
            console.log(error);
        }
    }, [key]);

    const setValue = (value: T) => {
        try {
            setStoredValue(value);
            window.localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.log(error);
        }
    };

    return [storedValue, setValue];
}
export default useLocalStorage;

function useLocalStorageForm<T>(
    key: string,
    initialValue: T
): [T, (value: T) => void, boolean, (value: boolean) => void] {
    if (typeof window === "undefined") {
        return [initialValue, () => {}, false, () => {}]; // SSR safe return
    }

    const [storedValue, setStoredValue] = useState<T>(initialValue);
    const [hasDiff, setHasDiff] = useState(false); // Flag for UI to react to differences
    const load = useRef(false);

    useEffect(() => {
        try {
            const localItem = window.localStorage.getItem(key);
            if (localItem) {
                const sessionStorageItem = sessionStorage.getItem(
                    key + "-temp"
                );
                const parsedLocalItem = JSON.parse(localItem);
                // Check for differences on first load
                if (
                    sessionStorageItem &&
                    sessionStorageItem !== JSON.stringify(initialValue)
                ) {
                    setHasDiff(true); // Notify UI there is a difference
                } else {
                    setHasDiff(false);
                }
                setStoredValue(parsedLocalItem);
            }
        } catch (error) {
            console.log("Error reading localStorage:", error);
        }
    }, [key, initialValue]);

    const setValue = (value: T) => {
        try {
            const currentLocalStorage = window.localStorage.getItem(key);

            // Store existing localStorage in sessionStorage before updating (only during first load)
            if (currentLocalStorage && !load.current) {
                console.log(
                    "Backing up current localStorage:",
                    JSON.parse(currentLocalStorage)
                );
                window.sessionStorage.setItem(
                    `${key}-temp`,
                    currentLocalStorage
                );
            }
            setStoredValue(value);
            window.localStorage.setItem(key, JSON.stringify(value));
            // After the first load, stop updating sessionStorage
            load.current = true;
        } catch (error) {
            console.log("Error setting localStorage:", error);
        }
    };
    return [storedValue, setValue, hasDiff, setHasDiff]; // Expose hasDiff for UI logic
}
